import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, Phone, Building, AlertCircle, Loader2, Share2, CheckCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import CompanySearch from '../components/CompanySearch';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, sendCode, verifyCode, completeProfile, token } = useAuth();

  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/my-bookings';

  // Steps: 'email' → 'code' → 'profile' (passwordless new users) or 'company' (optional)
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isNewUser, setIsNewUser] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [devCode, setDevCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminData, setAdminData] = useState({ email: '', password: '' });

  // Profile form
  const [profile, setProfile] = useState({ name: '', phone: '', company: '', referral_code: '' });

  const codeRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  // ==================== STEP 1: EMAIL ====================
  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (!email.includes('@')) { setError('Bitte geben Sie eine gültige E-Mail ein.'); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await sendCode(email);
      // If admin/employee → redirect to password login
      if (result.is_admin) {
        setAdminData({ email: email, password: '' });
        setShowAdminLogin(true);
        setLoading(false);
        return;
      }
      setIsNewUser(result.is_new_user);
      if (result._dev_code) setDevCode(result._dev_code);
      setStep('code');
      setCountdown(60);
      setCode(['', '', '', '', '', '']);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== STEP 2: CODE ====================
  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    // Auto-focus next
    if (value && index < 5) codeRefs.current[index + 1]?.focus();
    // Auto-submit when all 6 digits entered
    if (newCode.every(d => d) && value) {
      setTimeout(() => handleVerifyCode(newCode.join('')), 100);
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newCode = pasted.split('');
      setCode(newCode);
      codeRefs.current[5]?.focus();
      setTimeout(() => handleVerifyCode(pasted), 100);
    }
  };

  const handleVerifyCode = async (fullCode) => {
    const codeStr = fullCode || code.join('');
    if (codeStr.length !== 6) { setError('Bitte geben Sie den 6-stelligen Code ein.'); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await verifyCode(email, codeStr);
      if (result.is_new_user) {
        setTempToken(result.temp_token);
        setStep('profile');
      } else {
        // Existing user — logged in
        if (result.user?.role === 'admin' || result.user?.role === 'employee') {
          navigate('/admin');
        } else {
          navigate(redirectTo);
        }
      }
    } catch (err) {
      setError(err.message);
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // ==================== STEP 3: PROFILE ====================
  const handleCompleteProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) { setError('Name ist erforderlich.'); return; }
    setLoading(true);
    setError(null);
    try {
      await completeProfile(tempToken, profile);
      setStep('company');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== ADMIN LOGIN ====================
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const userData = await login(adminData.email, adminData.password);
      navigate(userData?.role === 'admin' ? '/admin' : redirectTo);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const slideVariant = {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: -30, transition: { duration: 0.2 } },
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 bg-[hsl(var(--surface-2))]">
      <SEO title="Anmelden" description="Melden Sie sich bei ZO Werbefläche an." noindex={true} />
      <div className="container mx-auto px-4 max-w-md">
        <AnimatePresence mode="wait">

          {/* ═══ ADMIN LOGIN ═══ */}
          {showAdminLogin ? (
            <motion.div key="admin" {...slideVariant}>
              <Card className="card-shadow">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl flex items-center justify-center gap-2">
                    <ShieldCheck className="w-6 h-6" /> Admin-Login
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAdminLogin} className="space-y-4">
                    {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                    <div className="space-y-2">
                      <Label>E-Mail</Label>
                      <Input type="email" value={adminData.email} onChange={(e) => setAdminData({...adminData, email: e.target.value})} required data-testid="admin-email" />
                    </div>
                    <div className="space-y-2">
                      <Label>Passwort</Label>
                      <Input type="password" value={adminData.password} onChange={(e) => setAdminData({...adminData, password: e.target.value})} required data-testid="admin-password" />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-btn">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                      Anmelden
                    </Button>
                    <Button variant="ghost" className="w-full text-sm" onClick={() => { setShowAdminLogin(false); setError(null); }}>
                      <ArrowLeft className="w-4 h-4 mr-2" /> Zurück
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          ) : step === 'email' ? (
            /* ═══ STEP 1: EMAIL ═══ */
            <motion.div key="email" {...slideVariant}>
              <Card className="card-shadow">
                <CardHeader className="text-center">
                  <div className="w-14 h-14 bg-[hsl(var(--primary)/0.1)] rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-7 h-7 text-[hsl(var(--primary))]" />
                  </div>
                  <CardTitle className="text-2xl">Willkommen</CardTitle>
                  <CardDescription>Geben Sie Ihre E-Mail ein um fortzufahren</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSendCode} className="space-y-5">
                    {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                    <div className="space-y-2">
                      <Label>E-Mail-Adresse</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          placeholder="ihre@email.ch"
                          autoFocus
                          required
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="send-code-btn">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Code senden
                    </Button>
                    <p className="text-xs text-center text-[hsl(var(--muted-foreground))]">
                      Wir senden Ihnen einen 6-stelligen Bestätigungscode per E-Mail.
                    </p>
                  </form>
                  <div className="mt-4 pt-4 border-t text-center">
                    <button
                      onClick={() => { setShowAdminLogin(true); setError(null); }}
                      className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                      data-testid="admin-login-link"
                    >
                      Admin-Login mit Passwort
                    </button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : step === 'code' ? (
            /* ═══ STEP 2: CODE ═══ */
            <motion.div key="code" {...slideVariant}>
              <Card className="card-shadow">
                <CardHeader className="text-center">
                  <div className="w-14 h-14 bg-[hsl(var(--chart-2)/0.1)] rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <ShieldCheck className="w-7 h-7 text-[hsl(var(--chart-2))]" />
                  </div>
                  <CardTitle className="text-2xl">Code eingeben</CardTitle>
                  <CardDescription>
                    Bestätigungscode an <strong>{email}</strong> gesendet
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

                  {devCode && (
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertDescription className="text-amber-700 text-xs">
                        Dev-Code: <strong className="font-mono tracking-widest">{devCode}</strong>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* 6-digit code input */}
                  <div className="flex justify-center gap-2.5" onPaste={handleCodePaste}>
                    {code.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (codeRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(i, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(i, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-2 rounded-xl focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.2)] outline-none transition-all"
                        autoFocus={i === 0}
                        data-testid={`code-digit-${i}`}
                      />
                    ))}
                  </div>

                  <Button
                    onClick={() => handleVerifyCode()}
                    className="w-full"
                    size="lg"
                    disabled={loading || code.some(d => !d)}
                    data-testid="verify-code-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Bestätigen
                  </Button>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => { setStep('email'); setError(null); setDevCode(''); }}
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] flex items-center gap-1"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> Andere E-Mail
                    </button>
                    <button
                      onClick={handleSendCode}
                      disabled={countdown > 0 || loading}
                      className={`${countdown > 0 ? 'text-[hsl(var(--muted-foreground))]' : 'text-[hsl(var(--primary))] hover:underline'}`}
                      data-testid="resend-code-btn"
                    >
                      {countdown > 0 ? `Erneut senden (${countdown}s)` : 'Code erneut senden'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : step === 'profile' ? (
            /* ═══ STEP 3: PROFILE ═══ */
            <motion.div key="profile" {...slideVariant}>
              <Card className="card-shadow">
                <CardHeader className="text-center">
                  <div className="w-14 h-14 bg-[hsl(var(--primary)/0.1)] rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <User className="w-7 h-7 text-[hsl(var(--primary))]" />
                  </div>
                  <CardTitle className="text-2xl">Profil erstellen</CardTitle>
                  <CardDescription>Fast geschafft! Vervollständigen Sie Ihr Profil.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCompleteProfile} className="space-y-4">
                    {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <Input value={profile.name} onChange={(e) => setProfile({...profile, name: e.target.value})} className="pl-10" placeholder="Max Muster" required autoFocus data-testid="profile-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Telefon</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} className="pl-10" placeholder="+41 79 123 45 67" data-testid="profile-phone" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Firma</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <Input value={profile.company} onChange={(e) => setProfile({...profile, company: e.target.value})} className="pl-10" placeholder="Firma AG" data-testid="profile-company" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Empfehlungscode</Label>
                      <div className="relative">
                        <Share2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        <Input value={profile.referral_code} onChange={(e) => setProfile({...profile, referral_code: e.target.value})} className="pl-10 font-mono uppercase" placeholder="z.B. A1B2C3D4" data-testid="profile-referral" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="complete-profile-btn">
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Konto erstellen
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          ) : step === 'company' ? (
            /* ═══ STEP 4: COMPANY (optional) ═══ */
            <motion.div key="company" {...slideVariant}>
              <Card className="card-shadow">
                <CardHeader className="text-center">
                  <div className="w-14 h-14 bg-[hsl(var(--chart-2)/0.1)] rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-7 h-7 text-[hsl(var(--chart-2))]" />
                  </div>
                  <CardTitle className="text-2xl">Konto erstellt!</CardTitle>
                  <CardDescription>Verknuepfen Sie Ihre Firma für regionale Rabatte (optional)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CompanySearch token={token} onVerified={() => {}} />
                  <div className="pt-2 border-t">
                    <Button className="w-full" onClick={() => navigate(redirectTo)} data-testid="skip-company-btn">
                      Weiter
                    </Button>
                    <p className="text-xs text-center text-[hsl(var(--muted-foreground))] mt-2">
                      Sie können die Firma später im Profil verknuepfen.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <p className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-8">
          Platform developed and operated by{' '}
          <a
            href="https://www.invictaflow.ch"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[hsl(var(--foreground))] underline underline-offset-2"
            data-testid="auth-footer-invictax-link"
          >
            InvictaX Group GmbH
          </a>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
